import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

const CompteRenduModal = ({ open, onClose, compteRendu }) => {
  if (!compteRendu) return null;

  const st = (compteRendu.statut || '').toLowerCase();
  const isValide = st === 'valide' || st === 'validé';
  const isBrouillon = st === 'brouillon' || st === 'draft';

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        id: 'printable-modal',
        sx: {
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.15)',
          overflow: 'hidden'
        }
      }}
    >
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-modal, #printable-modal * {
              visibility: visible;
            }
            #printable-modal {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              box-shadow: none !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      {/* En-tête Modal */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#4f46e5', width: 42, height: 42 }}>
            <AssignmentOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#0f172a' }}>
              Compte Rendu Médical
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {compteRendu.numero_seance || 'Séance'} • Réf #{compteRendu.id_compte_rendu || compteRendu.id}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" className="no-print">
          <Chip
            label={isValide ? 'Validé' : isBrouillon ? 'Brouillon' : 'En attente'}
            sx={{
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: '20px',
              backgroundColor: isValide ? '#dcfce7' : isBrouillon ? '#ffedd5' : '#fef3c7',
              color: isValide ? '#15803d' : isBrouillon ? '#c2410c' : '#b45309'
            }}
          />
          <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Contenu du Document */}
      <DialogContent sx={{ p: 4, backgroundColor: '#ffffff' }}>
        
        {/* Cartes d'informations */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', width: 36, height: 36 }}>
                <PersonOutlineIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#64748b', textTransform: 'uppercase' }}>
                  Patient / Client
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                  {compteRendu.client?.prenom} {compteRendu.client?.nom}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {compteRendu.client?.email || 'Aucun email'}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a', width: 36, height: 36 }}>
                <MedicalServicesOutlinedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#64748b', textTransform: 'uppercase' }}>
                  Praticien
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                  {compteRendu.praticien_nom}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Soin : {compteRendu.info_offre?.titre || compteRendu.pratique || 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarTodayOutlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#334155' }}>
              Date séance : {compteRendu.date_seance ? new Date(compteRendu.date_seance).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Observations textuelles */}
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f172a', mb: 1 }}>
              Perception du patient
            </Typography>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', backgroundColor: '#f8fafc', borderLeft: '4px solid #4f46e5' }}>
              <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                {compteRendu.perception_patient || 'Aucune information renseignée.'}
              </Typography>
            </Paper>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f172a', mb: 1 }}>
              Résumé de la séance
            </Typography>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', backgroundColor: '#f8fafc', borderLeft: '4px solid #0284c7' }}>
              <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                {compteRendu.resume_seance || 'Aucun résumé renseigné.'}
              </Typography>
            </Paper>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f172a', mb: 1 }}>
              Prévision pour la suite
            </Typography>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', backgroundColor: '#f8fafc', borderLeft: '4px solid #16a34a' }}>
              <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                {compteRendu.prevision_suite || 'Aucune prévision renseignée.'}
              </Typography>
            </Paper>
          </Box>
        </Stack>

      </DialogContent>

      <DialogActions className="no-print" sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPDF}
          sx={{
            borderColor: '#dc2626',
            color: '#dc2626',
            borderRadius: '50px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#fee2e2', borderColor: '#b91c1c' }
          }}
        >
          Télécharger / Imprimer PDF
        </Button>

        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#0f172a',
            borderRadius: '50px',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            '&:hover': { backgroundColor: '#1e293b' }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompteRenduModal;